import React from 'react'
import { Button, Checkbox, Col, Flex, Form, Input, Row, Select, Tooltip, Typography } from 'antd'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CalendarIcon, ResetIconDisabled } from 'src/assets/icons'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'

import './index.scss'
import { ExclamationCircleOutlined } from '@ant-design/icons'

interface Props {
  control: any
  fileContainsIssuedDate: boolean
  dateInFileDontIncludeSeparators: boolean
  columnList: any
  getValues: any
}

function MappingFields(props: Props) {
  const { t } = useTranslation()

  const lookupValues = useSelector(getLookupValues)

  const { DateFormat, DateFormatWithSeparator } = lookupValues || {}

  const { control, fileContainsIssuedDate, columnList, getValues, dateInFileDontIncludeSeparators } = props

  const filteredColumnList = (fieldName: string) => {
    const fieldList = ['checkNumber', 'amount', 'issuedDate', 'accountNumber', 'issuedPayee']?.filter(
      (item: string) => item !== fieldName,
    )

    const selectedValueList = fieldList?.map((field: string) => getValues(field))

    return columnList?.filter((item: any) => !selectedValueList?.includes(item?.label))
  }

  return (
    <>
      <Flex className='mapping-fields-header'>
        <Typography className='mapping-fields-title'>
          {t('FileMappingSetup:addFileMapping.title.mappingFields')}
        </Typography>

        <Button className='reset-button' disabled>
          <ResetIconDisabled />
          {t('OverallFunctionality:reset_btn')}
        </Button>
      </Flex>
      <Flex vertical className='mapping-fields-container'>
        <Row gutter={24} align='middle'>
          <Col span={24}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.checkNumber')} className='isRequired'>
              <Controller
                name='checkNumber'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t('FileMappingSetup:addFileMapping.placeholder.selectColumn')}
                    options={filteredColumnList('checkNumber')}
                  />
                )}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24} align='middle'>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.amount')} className='isRequired'>
              <Controller
                name='amount'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t('FileMappingSetup:addFileMapping.placeholder.selectColumn')}
                    options={filteredColumnList('amount')}
                  />
                )}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24} align='middle'>
          {fileContainsIssuedDate && (
            <Col span={12}>
              <Form.Item label={t('FileMappingSetup:addFileMapping.label.issuedDate')} className='isRequired'>
                <Controller
                  name='issuedDate'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder={t('FileMappingSetup:addFileMapping.placeholder.selectColumn')}
                      options={filteredColumnList('issuedDate')}
                    />
                  )}
                  disabled
                />
              </Form.Item>
            </Col>
          )}
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.datesInFile')} className='isRequired'>
              <Row gutter={8}>
                <Col span={5}>
                  <Controller
                    name='dateInFileDontIncludeSeparators'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={[
                          { value: false, label: 'No' },
                          { value: true, label: 'Yes' },
                        ]}
                      />
                    )}
                    disabled
                  />
                </Col>
                <Col span={19}>
                  <Controller
                    name='issuedDateFormat'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder={t('FileMappingSetup:addFileMapping.placeholder.issuedDateFormat')}
                        options={dateInFileDontIncludeSeparators ? DateFormatWithSeparator : DateFormat}
                      />
                    )}
                    disabled
                  />
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={
                <Tooltip
                  placement='right'
                  title={t('FileMappingSetup:addFileMapping.tooltip.accountNumber')}
                  color='white'
                  trigger='hover'
                  arrow={false}
                >
                  <Flex gap={9}>
                    {t('FileMappingSetup:addFileMapping.label.accountNumber')}
                    <ExclamationCircleOutlined />
                  </Flex>
                </Tooltip>
              }
            >
              <Controller
                name='accountNumber'
                control={control}
                render={({ field }) => <Select {...field} options={filteredColumnList('accountNumber')} />}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.issuedPayee')}>
              <Controller
                name='issuedPayee'
                control={control}
                render={({ field }) => <Select {...field} options={filteredColumnList('issuedPayee')} />}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.dateCreated')}>
              <Controller
                name='dateCreated'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    className='ant-input-view-only'
                    suffix={<CalendarIcon className='calendar-icon' />}
                  />
                )}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.dateUpdated')}>
              <Controller
                name='dateUpdated'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    className='ant-input-view-only'
                    suffix={<CalendarIcon className='calendar-icon' />}
                  />
                )}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
      </Flex>
    </>
  )
}

export default MappingFields
